/**
 * WEB-2704 — копирование экрана и блока в конструкторе: чистые помощники
 * хранилища редактора (без React и без хранилища).
 *
 * Правило копии — «у копии нет ни одного общего идентификатора с оригиналом»:
 * свежий id получает сам экран, каждый блок и КАЖДЫЙ вложенный элемент с
 * ключом `id` на любой глубине (варианты ответа, варианты-картинки, карточки,
 * плашки, строки, зоны). Обход общий, без перечня полей: перечень молчал бы
 * про новый вложенный список с id (шрам WEB-1687 — «ручной список молчит про
 * новое поле по построению»). Проверено по типам кабинета (`types/blocks.ts`)
 * и схемам сервера (`ai-funnels/block-schema`): ключ `id` внутри блока везде —
 * собственный идентификатор элемента, ссылки на чужое лежат в полях с другими
 * именами (`nextScreenId`, `*PriceId`, `fontId`) и сохраняются как есть.
 *
 * Переходы «куда ведёт» (`nextScreenId` и родня) копируются: копия экрана
 * ведёт туда же, куда оригинал. Правила «Логики» не копируются вовсе — они
 * живут отдельным списком и ссылаются на id оригинала, поэтому остаются на
 * нём (решение карточки: иначе переходы раздвоятся). `screenHasLogicRefs`
 * говорит кабинету, стоит ли сообщить об этом автору.
 *
 * WEB-3326 — имя переменной ответа (`variableName`, подстановка `{{имя}}`)
 * у копии не повторяет оригинал: плеер строит реестр «имя → блок»
 * (`collectNamedVariables` в `player/variable-context.ts`), и при двух
 * одинаковых именах побеждает последний блок — `{{goal}}` у покупателя
 * читал бы ответ копии. Поэтому копия получает свободное имя той же основы
 * (`goal` → `goal_2`, занято — `goal_3`), связь не пропадает молча, а автор
 * видит новое имя в поле блока. Занятые имена воронки — обязательный вход
 * обеих функций копирования: без них копию не сделать.
 */
import type { Rule } from '@/logic-engine';
import type { EditorBlock } from './types/blocks';
import type { EditorScreen } from './types/screen';
import { generateId } from './ids';

type IdFactory = () => string;

/**
 * Поля блока со ссылками на варианты ответа СВОЕГО экрана (строка или список
 * строк) — зеркало `BLOCK_OPTION_REF_FIELDS` сервера
 * (`backend/src/ai-funnels/persistent-id/reference-fields.ts`), сверяется
 * сторожем `block-ref-fields-schema-mirror.spec.ts`. Копия экрана выдаёт
 * вариантам новые id — ссылка обязана уехать вместе с ними, иначе подсказка
 * `micro_feedback` в копии не появится никогда (свежий взгляд волны 4,
 * WEB-3718). Копия одного блока на том же экране ссылки не трогает: варианты
 * экрана те же.
 */
export const BLOCK_OPTION_REF_FIELDS = ['revealOptionIds'] as const;

/**
 * Глубокая копия, в которой каждому ключу `id` (строке) выдан свежий id;
 * `idMap` (если дан) получает пары «старый → новый».
 */
function cloneFreshIds<T>(
  value: T,
  makeId: IdFactory,
  idMap?: Map<string, string>,
): T {
  if (Array.isArray(value)) {
    return value.map((item) =>
      cloneFreshIds(item, makeId, idMap),
    ) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(
      value as Record<string, unknown>,
    )) {
      if (key === 'id' && typeof item === 'string') {
        const next = makeId();
        if (idMap && !idMap.has(item)) idMap.set(item, next);
        out[key] = next;
      } else {
        out[key] = cloneFreshIds(item, makeId, idMap);
      }
    }
    return out as T;
  }
  return value;
}

/** Переставить ссылки блока на варианты по карте копии экрана. */
function withRemappedOptionRefs(
  block: EditorBlock,
  idMap: ReadonlyMap<string, string>,
): EditorBlock {
  const out: Record<string, unknown> = { ...block };
  const swap = (entry: unknown) =>
    typeof entry === 'string' ? (idMap.get(entry) ?? entry) : entry;
  for (const field of BLOCK_OPTION_REF_FIELDS) {
    const value = out[field];
    if (typeof value === 'string') out[field] = swap(value);
    else if (Array.isArray(value)) out[field] = value.map(swap);
  }
  return out as unknown as EditorBlock;
}

/**
 * Имена переменных ответа, занятые в воронке: те же блоки, что читает плеер
 * (`collectNamedVariables` — блоки верхнего уровня каждого экрана).
 */
export function funnelVariableNames(
  content: readonly EditorScreen[],
): Set<string> {
  const out = new Set<string>();
  for (const screen of content) {
    for (const block of screen.blocks ?? []) {
      const name = block.variableName;
      if (typeof name === 'string' && name.trim().length > 0) out.add(name);
    }
  }
  return out;
}

/** Хвост `_N` у имени: копия `goal_2` считает от основы `goal`. */
const NUMBERED_NAME = /^(.+)_(\d+)$/;

/**
 * Свободное имя для копии: основа + `_N`, наименьшее N от 2, не занятое.
 * Новое имя сразу попадает в `taken` — следующий блок той же копии экрана
 * его не получит. Блок без имени — как есть.
 */
function withFreeVariableName(
  block: EditorBlock,
  taken: Set<string>,
): EditorBlock {
  const name = block.variableName;
  if (typeof name !== 'string' || name.trim().length === 0) return block;
  taken.add(name);
  const base = NUMBERED_NAME.exec(name)?.[1] ?? name;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  const free = `${base}_${n}`;
  taken.add(free);
  return { ...block, variableName: free };
}

/**
 * Копия блока: всё как у оригинала, все id — новые, имя переменной ответа —
 * свободное среди `takenNames` (имена воронки, `funnelVariableNames`).
 */
export function cloneBlockWithFreshIds(
  block: EditorBlock,
  takenNames: ReadonlySet<string>,
  makeId: IdFactory = generateId,
): EditorBlock {
  return withFreeVariableName(
    cloneFreshIds(block, makeId),
    new Set(takenNames),
  );
}

/**
 * Копия экрана под новым именем (имена переменных ответа блоков — свободные
 * среди `takenNames`, у каждого блока копии своё). Пометка «экран после оплаты» не переходит:
 * такой экран у пейволла один (инвариант `setScreenPostPurchase`), копия с
 * пометкой молча сняла бы её с оригинала при следующем переключении.
 */
export function cloneScreenWithFreshIds(
  screen: EditorScreen,
  name: string,
  takenNames: ReadonlySet<string>,
  makeId: IdFactory = generateId,
): EditorScreen {
  const { isPostPurchase: _dropped, ...rest } = screen;
  const idMap = new Map<string, string>();
  const copy = cloneFreshIds(rest, makeId, idMap);
  const taken = new Set(takenNames);
  const blocks = copy.blocks.map((block) =>
    withRemappedOptionRefs(withFreeVariableName(block, taken), idMap),
  );
  // WEB-3376: карты «старое → новое» только для блоков этого дубля.
  const names = new Map<string, string>();
  const blockIds = new Map<string, string>();
  screen.blocks.forEach((original, i) => {
    const next = blocks[i];
    if (!next) return;
    blockIds.set(original.id, next.id);
    const was = original.variableName;
    if (typeof was === 'string' && was.trim() && next.variableName !== was)
      names.set(was, next.variableName as string);
  });
  return {
    ...copy,
    name,
    blocks: withRemappedPlaceholders(blocks, names, blockIds),
  };
}

/** Подстановка `{{…}}` — тот же разбор, что у плеера (`player/interpolate.ts`). */
const PLACEHOLDER = /\{\{([^}]+)\}\}/g;
const ANSWER_PREFIX = 'answer.';

/**
 * WEB-3376: подстановки внутри копии экрана — на блоки копии. `{{goal}}`
 * переезжает на новое имя блока того же дубля (`goal_2`), `{{answer.<id>}}` —
 * на новый id блока того же дубля (поправка скептика 6). Имена и id блоков
 * других экранов не трогаются: подстановка на чужой экран в копии ведёт туда
 * же, куда в оригинале. Обход — по всем строкам копии, без перечня полей.
 */
function withRemappedPlaceholders<T>(
  value: T,
  names: ReadonlyMap<string, string>,
  blockIds: ReadonlyMap<string, string>,
): T {
  if (typeof value === 'string') {
    if (!value.includes('{{')) return value;
    return value.replace(PLACEHOLDER, (whole, inner: string) => {
      const key = inner.trim();
      const renamed = names.get(key);
      if (renamed !== undefined) return `{{${renamed}}}`;
      if (key.startsWith(ANSWER_PREFIX)) {
        const next = blockIds.get(key.slice(ANSWER_PREFIX.length));
        if (next !== undefined) return `{{${ANSWER_PREFIX}${next}}}`;
      }
      return whole;
    }) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) =>
      withRemappedPlaceholders(item, names, blockIds),
    ) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>))
      out[key] = withRemappedPlaceholders(item, names, blockIds);
    return out as T;
  }
  return value;
}

interface GroupLike {
  items?: unknown[];
}

function groupHasAnswerRef(
  group: GroupLike | undefined,
  blockIds: Set<string>,
): boolean {
  if (!group || !Array.isArray(group.items)) return false;
  return group.items.some((item) => {
    if (item === null || typeof item !== 'object') return false;
    const node = item as {
      items?: unknown[];
      source?: unknown;
      source_ref?: unknown;
    };
    if (Array.isArray(node.items)) return groupHasAnswerRef(node, blockIds);
    return (
      node.source === 'ANSWER' &&
      typeof node.source_ref === 'string' &&
      blockIds.has(node.source_ref)
    );
  });
}

/**
 * Есть ли в «Логике» правила, привязанные к экрану: правило самого экрана или
 * его блока, переход на этот экран, условие по ответу на его блок. Ссылки —
 * по реестру сервера (`persistent-id/reference-fields.ts`): `source_ref` —
 * id блока только у условия по ответу.
 */
export function screenHasLogicRefs(
  logic: readonly Rule[] | undefined,
  screen: EditorScreen,
): boolean {
  if (!Array.isArray(logic) || logic.length === 0) return false;
  const blockIds = new Set(screen.blocks.map((b) => b.id));
  return logic.some((rule) => {
    if (rule.scope_ref === screen.id || blockIds.has(rule.scope_ref))
      return true;
    const action = rule.action as { type: string; target?: unknown };
    if (
      (action.type === 'GOTO_SCREEN' || action.type === 'GOTO_ENDING') &&
      action.target === screen.id
    ) {
      return true;
    }
    return groupHasAnswerRef(rule.condition_group as GroupLike, blockIds);
  });
}
